/**
 * `withRouter` HOC — the load-bearing piece of the react-router v3 shim.
 *
 * RR3 injected `{router, location, params, route, routes}` into wrapped
 * components. We reproduce that prop bag by reading TanStack hooks in a function
 * wrapper and forwarding them, so the ~60 `withRouter`-wrapped (mostly class)
 * components keep reading `this.props.*` unchanged.
 *
 * Everything is derived at render (not synced via effects) and memoized so the
 * injected objects keep a stable identity across unrelated re-renders — see
 * docs/src/marketplace/{use-effect-usage,rerendering-best-practices}.md.
 */
import * as React from 'react';
import {useMemo} from 'react';

import {browserHistory, routerHistory} from './history';
import {LayoutInjectedPropsContext} from './layoutContext';
import {toShimLocation} from './path';
import {useRouterSnapshot} from './useRouterSnapshot';
import type {RouteMeta, WithRouterProps} from './types';

interface MatchLike {
  routeId?: string;
  fullPath?: string;
  staticData?: Record<string, unknown>;
}

type RoutesById = Record<string, {path?: string}> | undefined;

// Stable empty array for the no-RouterProvider fallback, so `routes`/`params`
// memoization keys don't change identity on every render in that path.
const EMPTY_MATCHES: ReadonlyArray<never> = [];

function toRouteMeta(match: MatchLike, routesById: RoutesById): RouteMeta {
  // RR3 injected the route's RELATIVE path segment (e.g. 'overview'); TanStack's
  // match exposes the absolute fullPath, so resolve the relative path by id.
  const relativePath = match.routeId ? routesById?.[match.routeId]?.path : undefined;
  return {
    path: relativePath ?? match.fullPath,
    ...(match.staticData ?? {})
  };
}

function toRouteMetas(matches: ReadonlyArray<unknown>, routesById: RoutesById): RouteMeta[] {
  return (matches as MatchLike[]).map((m) => toRouteMeta(m, routesById));
}

/** RR3 exposed splat as `params.splat`; TanStack uses `params._splat`. */
function normalizeParams(raw: Record<string, unknown>): Record<string, string> {
  const params: Record<string, string> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (value == null) {
      continue;
    }
    params[key] = String(value);
    if (key === '_splat') {
      params.splat = String(value);
    }
  }
  return params;
}

/** Hook form: returns the same prop bag `withRouter` injects. */
export function useRouterShim(): WithRouterProps {
  const {router, state} = useRouterSnapshot();
  // Props a parent layout injected into its child (RR3 cloneElement bridge).
  const layoutProps = React.useContext(LayoutInjectedPropsContext);

  // With a RouterProvider, read the live router state; without one (e.g. a plain
  // component test), fall back to the global history so nothing throws.
  const location = state ? state.location : routerHistory.location;
  const matches = state ? state.matches : EMPTY_MATCHES;
  // ParsedLocation exposes `searchStr`; HistoryLocation exposes `search`.
  const searchStr = 'searchStr' in location ? location.searchStr : location.search;
  const pathname = location.pathname;
  const hash = location.hash;
  const locState = location.state;
  const href = 'href' in location ? location.href : undefined;

  const shimLocation = useMemo(
    () => toShimLocation({pathname, search: searchStr, hash, href, state: locState}),
    [pathname, searchStr, hash, href, locState]
  );

  const routesById = router?.routesById as RoutesById;
  const routes = useMemo(() => toRouteMetas(matches, routesById), [matches, routesById]);
  const route = useMemo(() => (routes.length > 0 ? routes[routes.length - 1] : {}), [routes]);
  const leaf = matches.length > 0 ? matches[matches.length - 1] : undefined;
  const params = useMemo(
    () => normalizeParams((leaf?.params ?? {}) as Record<string, unknown>),
    [leaf]
  );

  // RR3's `this.props.router` carried the current match's params/routes/location
  // alongside the navigation methods. Augment the shared browserHistory facade
  // with them (the standalone singleton omits them). MEMOIZED: RR3's injected
  // `router` was a stable singleton, so consumer `useEffect(..., [router])` ran
  // once; if we rebuilt it every render, those effects (e.g. ConfirmLeave's
  // setRouteLeaveHook, useFormRouteHandler's `router.replace`) would re-fire on
  // every render — re-registering leave hooks / re-issuing navigations in a loop.
  const injectedRouter = useMemo(
    () => ({...browserHistory, params, routes, location: shimLocation}),
    [params, routes, shimLocation]
  );

  // Memoize the whole prop bag too, so the identity stays stable across unrelated
  // re-renders (the file's contract — see rerendering-best-practices.md). Layout-
  // injected props come first so the router props always win on conflict.
  return useMemo(
    () => ({
      ...layoutProps,
      router: injectedRouter,
      location: shimLocation,
      params,
      routeParams: params,
      route,
      routes
    }),
    [layoutProps, injectedRouter, shimLocation, params, route, routes]
  );
}

// One wrapper per wrapped component, kept for the lifetime of the module.

const wrapperCache = new WeakMap<React.ComponentType<any>, React.FC<any>>();

/**
 * Wrap a component so it receives RR3's `{router, location, params, route,
 * routes}` props. Mirrors RR3's `withRouter(Component)`.
 *
 * The injected props are made optional on the resulting component (RR3 behavior:
 * the router provides them, but callers may still pass any of the component's
 * own props), so call sites and route-`component` assignments both type-check.
 *
 * ★ The wrapper is memoized per `Component`, so `withRouter(X)` always returns
 * the SAME reference. This matters for route `component`s: TanStack renders a
 * matched route's component with no remount key (see @tanstack/react-router
 * `Match`: `jsx(Comp, {}, undefined)`), so when you navigate between two sibling
 * routes that share a component, React reconciles it at the same Outlet position
 * and REUSES the instance — but only if the element type is referentially equal.
 * RR3 relied on exactly this (it used the raw `component={X}` reference for every
 * route), e.g. AssignmentSettings keeps its react-hook-form state when the
 * customize/done flow navigates `…/settings` ⇄ `…/classroom/:id/settings`.
 * Minting a fresh wrapper per route `component:` site would force a remount that
 * silently wipes such in-progress state, so callers can keep calling
 * `withRouter(X)` inline and still get a stable reference.
 */
export function withRouter<P extends object>(
  Component: React.ComponentType<P>
): React.FC<Partial<P>> {
  const cached = wrapperCache.get(Component);
  if (cached) {
    return cached as React.FC<Partial<P>>;
  }
  const WithRouter: React.FC<Partial<P>> = (ownProps) => {
    const injected = useRouterShim();
    // Explicitly-passed props win over injected ones (RR3 behavior), so callers
    // and tests can override router/location/params.
    return <Component {...({...injected, ...ownProps} as unknown as P)} />;
  };
  WithRouter.displayName = `withRouter(${Component.displayName || Component.name || 'Component'})`;
  wrapperCache.set(Component, WithRouter);
  return WithRouter;
}

export default withRouter;
